import { getEnglishModule } from '@uhg-psx/mapping-files-translation-transperfect';
import { get } from 'lodash';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import sortBy from 'lodash/sortBy';

import translationsEnJson from '../../public/locale/transperfect/en/translation.json';
import {
  AUTO_INCREMENT_SEARCH_RADIUS,
  CareCategories,
  Constants,
  DEFAULT_SEARCH_RADIUS,
  DEFAULT_SORT_BY,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  FilterKeysAndParamsMapping,
  NOT_AVAILABLE,
  ProviderTypes,
  ReverseCoverageTypesCodes,
  SORT_BY_RECOMMENDED_PROVIDERS,
  SortOptionValues,
  VirtualCareOptions,
  rollUpCodeMapping,
} from '../common/Constants';
import { ConstantsLagoon } from '../common/ConstantsLagoon';
import {
  buildBhGroupedAoeCategoriesCode,
  mapAoeCode,
  staticAoeCode,
} from '../common/ContentFilters/AllFilterDrawer/ProviderFilters/GroupedAoeFilter/groupedAoe.utils';
import {
  getBestMatchOptionKey,
  getRecommendedProviderOptionKey,
} from '../common/ContentFilters/SortByUtil';
import {
  getGeoLocationFromStorage,
  getMatchingVirtualCareEntries,
} from '../common/PSXHeader/SearchBar/utils';
import { getFeatureFlag } from '../common/Utils';
import {
  addDaysToDate,
  dateFormatUTC,
} from '../common/Utils/datesUtils/formatDate';
import {
  getAcoBenefitPlan,
  getSuppressCostEfficiencySmartChoice,
} from '../common/Widget/SmartChoiceWidget/utils';
import { getLanguage } from '../frontends/ProviderSearch/context/Internationalization/helpers';
import { getCategoryLLMFlag } from '../hooks/useCategoryLLMFlag/useCategoryLLMFlag';
import { useCurrentMemberOrDependentInfo } from '../hooks/useCurrentMember';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLagoon } from '../hooks/useLagoon';
import { usePortalShowEAPfilter } from '../hooks/usePortalShowEAPfilter';
import { StoreKeys } from '../hooks/useStore/state';
import { useStore } from '../hooks/useStore/useStore';
import { ProviderType } from '../models/ProviderDetails';
import {
  FiltersData,
  LastModifiedFilter,
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
  ProviderSearchRequestParams,
} from '../models/ProviderSearch';
import { DateTime } from '../utils/DateTime';
import {
  getBehavioralFacilityProgramsFromLagoon,
  getIntersectionFromArrays,
} from './common';
import { portalShouldShowOnlineScheduling } from './portalConfig.utils';
import { resolveIncludeSpecialityRollupCodes } from './providerDetails.utils';
import { isSuppressPremiumSmartchoice } from './smartChoice.utils';
import {
  getLoggedInMember,
  getMarketType,
  getProductCodeForCoverageType,
  getRulesPackageKey,
  isMemberEAPEligible,
} from './user.utils';

// Get BCC Provider Scheduling code from behavioral programs translations
const behaviouralProgramsTranslations = (getEnglishModule(
  'behaviouralProgramsTranslations'
) || {}) as Record<string, string>;
const BCC_PROVIDER_CODE: string =
  Object.keys(behaviouralProgramsTranslations).find(
    (key) => behaviouralProgramsTranslations[key] === 'Provider Scheduling'
  ) || 'P0018';

export type LagoonData = {
  id: string;
  key: string;
  pseudoRollupCode: string;
  dntDescription: string;
  action: string;
  filterName: string;
  linkTarget: string;
  providerType: string;
  categoryChips: string;
  coverageType: string;
  isPseudoRollupSearch: boolean;
  state?: string;
};

const specialtyCodes = require('../common/PSXHeader/specialtyCodes.json');

export const getValuesFromOptionsArray = (filter: Option[] = []) => {
  const values: any[] = [];
  filter?.forEach((option: Option) => {
    values.push(option.value);
  });
  return values;
};

export const getCommaSeparatedValues = (filter: Option[] = []) =>
  filter
    .filter((option) => option.value != null && option.value !== '')
    .map((option) => option.value)
    .join(',');

export const getValuesAsKeyFromOptionsArray = (filter: Option[] = []) => {
  const values = {};
  filter?.forEach((option: Option) => {
    const key = FilterKeysAndParamsMapping[option.value as string] as string;
    values[key] = true;
  });
  return values;
};

const getArrayTypeFilterValues = (
  params: ProviderSearchFilters,
  pickFirstValue = false,
  eapOffered: Option[] = [],
  eapBHProgramFlag = false
) => {
  const filterParams = {};
  Object.keys(params).forEach((key) => {
    if (params[key]?.length) {
      let updatedParams = params[key];
      if (
        key === FilterCollectionModel.AREA_OF_EXPERTISE &&
        eapBHProgramFlag &&
        eapOffered.length
      ) {
        updatedParams = updatedParams.filter(
          (option) => option?.dnt_label !== EAP_OPTION_LABEL
        );
      }
      const options = getValuesFromOptionsArray(updatedParams);

      // Filter out empty values for AreaOfExpertise
      const filteredOptions =
        key === FilterCollectionModel.AREA_OF_EXPERTISE
          ? options.filter((option) => option && option.trim() !== '')
          : options;

      filterParams[FilterKeysAndParamsMapping[key]] = pickFirstValue
        ? filteredOptions[0]
        : filteredOptions;
    }
  });
  return filterParams;
};

export const getDateRangeFromFilterValuesArray = (
  params: ProviderSearchFilters
) => {
  const filterParams = {};
  let filterParamsDateRange = {};
  const startDate: any = new Date(
    DateTime.getCurrentDateTime().getUTCFullYear(),
    DateTime.getCurrentDateTime().getUTCMonth(),
    DateTime.getCurrentDateTime().getUTCDate()
  );

  // Converting from IST to UTC is taking a day back. So, adding a day to the start date.
  if (
    DateTime.getCurrentDateTime()
      .toTimeString()
      .indexOf('India Standard Time') !== -1
  ) {
    startDate.setDate(startDate.getDate() + 1);
  }

  const formattedDocAsapStartDate = dateFormatUTC(startDate, 'yyyy-MM-dd');
  Object.keys(params).forEach((key) => {
    if (params[key]?.length) {
      const options = getValuesFromOptionsArray(params[key]);
      options.sort((a, b) => a - b);
      const farthestDate = options[options.length - 1];
      const endDate = addDaysToDate(startDate, farthestDate - 1);
      filterParams[FilterKeysAndParamsMapping[key]] = dateFormatUTC(
        endDate,
        'yyyy-MM-dd'
      );
      filterParamsDateRange = {
        docAsapStDate: formattedDocAsapStartDate,
        ...filterParams,
      };
    }
  });
  return filterParamsDateRange;
};

const getObjectTypeFilterValues = (params: ProviderSearchFilters) => {
  const filterParams = {};
  Object.keys(params).forEach((key) => {
    if (params[key]?.value)
      filterParams[FilterKeysAndParamsMapping[key]] = params[key].value;
  });
  return filterParams;
};

const getBehavioralFacilityProgramsValue = (options: any[]): string => {
  const result = options
    .map((option: any) => {
      let value = [];

      if (!option || option.length === 0) {
        return value;
      }

      // Remove duplicates
      value = option
        .map((item: any) => item?.value)
        .toString()
        .split(',');
      value = [...new Set(value)];
      return value;
    })
    .filter((item: any) => item.length > 0);

  return result.length > 1
    ? getIntersectionFromArrays(result).join(',')
    : result.join(',');
};

export const getVirtualCareOptionsFilterValues = (
  selectedVirtualCareOption: VirtualCareOptions | undefined
) => {
  switch (selectedVirtualCareOption) {
    case VirtualCareOptions.OFFERS_IN_PERSON:
      return {
        virtualOnlyLocationInd: false,
      };
    case VirtualCareOptions.OFFERS_VIRTUAL_VISITS:
      return {
        searchRadius: undefined,
        virtualCare: true,
      };
    case VirtualCareOptions.BOTH:
      return {
        virtualCare: true,
        virtualOnlyLocationInd: false,
      };
    default:
      return null;
  }
};

export const parseProviderSearchFilters = (
  selectedFilters: ProviderSearchFilters,
  coverageType: string
): ProviderSearchRequestParams => {
  const {
    AgesAreaOfExpertise,
    AcoName,
    CulturalCompetencyCode,
    AgeRange,
    ExpressAccessProviderIndicator,
    TieredProvider,
    TieredProviders,
    PremiumCarePhysician,
    PlatinumProviderIndicator,
    ProviderScheduling,
    VirtualCare,
    Distance,
    FacilityType,
    PreferredFacilities,
    Program,
    ProgramCategory,
    SpecialtyCode,
    AreaOfExpertise,
    PatientReviews,
    HealthSuppliesOffered,
    LanguageCode,
    Medical,
    HospitalAffiliations,
    EssentialCommunityProvider,
    AcceptingNewPatients,
    EveningAndWeekend,
    AccessibilityCode,
    GenderCode,
    PatientSafetyInfoAvailable,
    SortBy,
    EthnicityCode,
    RaceCode,
    BehavioralProgramIndicator,
    ServicesOffered,
    AccommodateOffered,
    LocationName,
    UpcomingAvailability,
    BoardCertifiedSpecialtyIndicator,
    PreferredProviders,
    VirtualCareOptions,
    EapOffered,
    AccreditationData,
    PlanType,
    NetworkAffiliations,
  } = selectedFilters;
  const CulturalCompetencyCodeStr =
    CulturalCompetencyCode?.map((item) => item.value).join() ?? '';
  const behavioralFacilityPrograms = getBehavioralFacilityProgramsValue([
    AgeRange,
    Program,
    ProgramCategory,
  ]);

  const [
    isBhBoardCertificationEnabled,
    eapBHProgramFlag,
    enableBhGroupedAoeFilter,
    enableNetworkAffiliationFlow,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BOARD_CERTIFIED_PROVIDERS_BH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW,
  ]);
  const allConfigs = useLagoon('config');

  const bhBoardCertificationCodes = getLagoonConfigValue(
    allConfigs,
    'BH_BOARD_CERTIFICATION_CODES'
  );

  const pediatricSpecialtyCodesRaw = getLagoonConfigValue(
    allConfigs,
    ConstantsLagoon.CONFIG.PEDIATRIC_SPECIALTY_CODES
  );
  const pediatricSpecialtyCodes = parseLagoonConfigArray(
    pediatricSpecialtyCodesRaw
  );

  const licensedInPediatricsToggle = selectedFilters['LicensedInPediatrics'];
  let effectiveSpecialtyCode = SpecialtyCode;
  if (licensedInPediatricsToggle?.value) {
    if (!SpecialtyCode || SpecialtyCode.length === 0) {
      const pediatricOptions = pediatricSpecialtyCodes.map((code) => ({
        label: `Pediatric Specialty ${code}`,
        value: code,
        dnt_label: `Pediatric Specialty ${code}`,
      }));
      effectiveSpecialtyCode = pediatricOptions;
    }
  }

  const objectTypeFilterValues = getObjectTypeFilterValues({
    ExpressAccessProviderIndicator,
    TieredProvider,
    PremiumCarePhysician,
    PlatinumProviderIndicator,
    ProviderScheduling,
    VirtualCare,
    SortBy,
    PreferredProviders,
  });

  const arrayTypeFilterValues = getArrayTypeFilterValues(
    {
      AgesAreaOfExpertise,
      AcoName,
      FacilityType,
      PreferredFacilities,
      SpecialtyCode: effectiveSpecialtyCode,
      AreaOfExpertise,
      LocationName,
      HealthSuppliesOffered,
      LanguageCode,
      AccessibilityCode,
      Medical,
      HospitalAffiliations,
      EthnicityCode,
      RaceCode,
      BehavioralProgramIndicator,
      ServicesOffered,
      AccreditationData,
      ProviderScheduling,
    },
    false,
    EapOffered,
    eapBHProgramFlag
  );

  const arrayTypeFilterValuesFromFirstIndex = getArrayTypeFilterValues(
    {
      PatientReviews,
      GenderCode,
      Distance,
      VirtualCare,
      PremiumCarePhysician,
      BoardCertifiedSpecialtyIndicator,
    },
    true
  );
  const arrayTypeFilterDateRange = getDateRangeFromFilterValuesArray({
    UpcomingAvailability,
  });

  const acceptingNewPatientsSelectedValue = getValuesFromOptionsArray(
    AcceptingNewPatients
  )?.includes('Y')
    ? true
    : getValuesFromOptionsArray(AcceptingNewPatients)?.includes('N')
    ? false
    : null;
  const planTypeFilterValue = getCommaSeparatedValues(PlanType);

  const networkAffiliationIdValue =
    getValuesFromOptionsArray(NetworkAffiliations);

  const tieredProviders = getValuesAsKeyFromOptionsArray(TieredProviders);

  const selectedVirtualCareOption =
    getValuesFromOptionsArray(VirtualCareOptions)?.[0];
  const virtualCareOptionsFilterValues = getVirtualCareOptionsFilterValues(
    selectedVirtualCareOption
  );

  const filterParams: any = {
    ...objectTypeFilterValues,
    ...arrayTypeFilterValues,
    ...(CulturalCompetencyCode?.length && {
      culturalCompetencyCode: CulturalCompetencyCodeStr,
    }),
    ...arrayTypeFilterValuesFromFirstIndex,
    ...arrayTypeFilterDateRange,
    ...tieredProviders,
    ...virtualCareOptionsFilterValues,
    ...(AccommodateOffered?.length && {
      accommodateOffered: getValuesFromOptionsArray(AccommodateOffered),
    }),
    ...(AcceptingNewPatients?.length && {
      acceptingNewPatients: acceptingNewPatientsSelectedValue,
    }),
    ...(ProviderScheduling?.value && {
      bccProviderCode: BCC_PROVIDER_CODE,
    }),
    ...(EssentialCommunityProvider?.length && {
      essentialCommunityProvider: getValuesFromOptionsArray(
        EssentialCommunityProvider
      ).includes('Y'),
    }),
    ...(EveningAndWeekend?.length &&
      getValuesFromOptionsArray(EveningAndWeekend).includes('E') && {
        eveningAvailability: true,
      }),
    ...(EveningAndWeekend?.length &&
      getValuesFromOptionsArray(EveningAndWeekend).includes('W') && {
        weekendAvailability: true,
      }),
    ...(PatientSafetyInfoAvailable?.length &&
      getValuesFromOptionsArray(PatientSafetyInfoAvailable).includes('Y') && {
        patientSafetyInfoAvailable: true,
      }),
    ...(BoardCertifiedSpecialtyIndicator?.length &&
      coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
      isBhBoardCertificationEnabled && {
        boardCertifiedSpecialtyCodes: bhBoardCertificationCodes,
      }),
    planTypeFilterValue,
    ...(behavioralFacilityPrograms?.length && { behavioralFacilityPrograms }),
    ...(enableNetworkAffiliationFlow &&
      networkAffiliationIdValue?.length && {
        networkAffiliationId: networkAffiliationIdValue,
      }),
  };

  const isBhGroupedAoeCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
  if (
    enableBhGroupedAoeFilter &&
    isBhGroupedAoeCoverage &&
    AreaOfExpertise?.length
  ) {
    const selectedAoeValues = AreaOfExpertise.map((item) => String(item.value));

    const aoeCategoriesCode = buildBhGroupedAoeCategoriesCode(
      selectedAoeValues,
      allConfigs
    );
    filterParams.aoeCodes = [];
    if (aoeCategoriesCode) {
      filterParams.aoeCategoriesCode = aoeCategoriesCode;
    }
  }

  filterParams.hospitalAffiliationProviderIds =
    filterParams.hospitalAffiliationProviderIds?.join(', ');
  return filterParams;
};

const getEveningAndWeekendFilter = (
  { EveningAvailability, WeekendAvailability }: FiltersData,
  translate,
  suppressVisionWeekendEveningsFilter
) => {
  if (suppressVisionWeekendEveningsFilter) {
    return [];
  }
  const eveningAvailability = getValuesFromOptionsArray(
    EveningAvailability?.options
  ).includes('Y')
    ? [
        {
          label: translate('FILTER_OPTIONS.EveningAvailability.Y'),
          value: 'E',
          dnt_label: translationsEnJson.FILTER_OPTIONS.EveningAvailability.Y,
        },
      ]
    : [];

  const weekendAvailability = getValuesFromOptionsArray(
    WeekendAvailability?.options
  ).includes('Y')
    ? [
        {
          label: translate('FILTER_OPTIONS.WeekendAvailability.Y'),
          value: 'W',
          dnt_label: translationsEnJson.FILTER_OPTIONS.WeekendAvailability.Y,
        },
      ]
    : [];

  return [...eveningAvailability, ...weekendAvailability];
};

export const getSnpType = (coverageType: string, medicalCoverage) =>
  coverageType == ReverseCoverageTypesCodes.MEDICAL
    ? medicalCoverage?.snpType
    : '';

const readOptionsFromFilterCollection = () => {
  const showOnlineScheduling = portalShouldShowOnlineScheduling();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const upcomingAvailability: boolean =
    showOnlineScheduling &&
    getFeatureFlag(
      featureFlags,
      ConstantsLagoon.FEATURE_FLAGS.UPCOMING_AVAILABILITY
    );
  const filterResults: string[] = [
    FilterCollectionModel.AGE_RANGE,
    FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR,
    FilterCollectionModel.PROGRAM,
    FilterCollectionModel.PROGRAM_CATEGORY,
    FilterCollectionModel.DISTANCE,
    FilterCollectionModel.VIRTUAL_CARE,
    FilterCollectionModel.PATIENT_REVIEWS,
    FilterCollectionModel.TIERED_PROVIDERS,
    FilterCollectionModel.BOARD_CERTIFIED_SPECIALITY_INDICATOR,
    FilterCollectionModel.PREMIUM_CARE_PHYSICIAN,
    FilterCollectionModel.PRIORITY_SCHEDULING,
    upcomingAvailability ? FilterCollectionModel.UPCOMING_AVAILABILITY : '',
  ];
  return filterResults;
};

const isVisionWeekendEveningsFilterEnabled = (coverageType) => {
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const visionWeekendEveningsFilterEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.VISION_WEEKEND_EVENINGS_FILTER_ENABLED
  );

  return (
    coverageType === ReverseCoverageTypesCodes.VISION &&
    !visionWeekendEveningsFilterEnabled
  );
};

const getModifiedFilterOptions = (lastModifiedFilter) =>
  lastModifiedFilter?.options || [];

const getBehavioralFacilityProgramsOptions = (
  filter,
  filtersData,
  translate,
  selectedFilters?: ProviderSearchFilters,
  bhProgramLagoonData?
) =>
  getBehavioralFacilityProgramsFromLagoon(filter.label, bhProgramLagoonData)
    .map((item) => ({
      label: translate(item.name),
      dnt_label: item.name,
      value: item.programIds?.replaceAll(' ', ''),
      displayOrder: item.displayOrder,
      isDisabled: !filtersData?.BehavioralFacilityProgram?.options.some(
        (option) => {
          const commonFilterValues = findCommonFilterValues(
            selectedFilters?.ProgramCategory,
            selectedFilters?.AgeRange
          );

          const optionValuesArr: string[] = option?.value?.includes(',')
            ? option.value.split(',').map((val) => val.trim())
            : [option?.value?.toString().trim()];

          for (const optionValue of optionValuesArr) {
            if (
              commonFilterValues &&
              !isFilterSelected(commonFilterValues, optionValue) &&
              filter.model == FilterCollectionModel.PROGRAM
            ) {
              continue;
            }

            if (item.programIds.includes(optionValue)) {
              return true;
            }
          }

          return false;
        }
      ),
    }))
    .sort((a, b) => a.displayOrder - b.displayOrder);

const findCommonFilterValues = (programCategoriesArray, ageRangeArray) => {
  if (!programCategoriesArray && !ageRangeArray) return null;

  if (!ageRangeArray) {
    return programCategoriesArray.flatMap((obj) =>
      obj.value.split(',').map((value) => value.trim())
    );
  }

  if (!programCategoriesArray) {
    return ageRangeArray.flatMap((obj) =>
      obj.value.split(',').map((value) => value.trim())
    );
  }

  const combinedProgramCategoryValues = programCategoriesArray.flatMap((obj) =>
    obj.value.split(',').map((value) => value.trim())
  );
  const combinedAgeRangeValues = ageRangeArray.flatMap((obj) =>
    obj.value.split(',').map((value) => value.trim())
  );

  return combinedProgramCategoryValues.filter((value) =>
    combinedAgeRangeValues.includes(value)
  );
};

const isFilterSelected = (commonFilterValues, optionValue): boolean => {
  if (commonFilterValues != null && commonFilterValues.length > 0) {
    return commonFilterValues.includes(optionValue);
  }

  return true;
};

const isBehavioralFacilityFilterModel = (filterModel: string): boolean =>
  [
    FilterCollectionModel.AGE_RANGE,
    FilterCollectionModel.PROGRAM,
    FilterCollectionModel.PROGRAM_CATEGORY,
    FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR,
  ].some((model) => model === filterModel);

const shouldApplySorting = (filter: any): boolean =>
  filter?.type !== 'radio' && !filter?.isSortingOptionsDisabled;

const getPlatinumOptionsFromLagoon = (translate) => {
  const uiMessaging = useLagoon('ui-messaging')();
  const platinumLabel = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PLATINUM_POPOVER_MESSAGE,
  });

  if (!platinumLabel) return [];

  return [
    {
      label: translate(platinumLabel?.message),
      value: true,
      dnt_label: '',
    },
  ];
};

const getProviderSchedulingOptionsFromLagoon = (translate) => {
  const uiMessaging = useLagoon('ui-messaging')();
  const providerSchedulingContent = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.PRIORITY_SCHEDULING_POPOVER_MESSAGE,
  });

  if (!providerSchedulingContent) return [];

  return [
    {
      label: translate(providerSchedulingContent?.message),
      value: true,
      dnt_label: '',
    },
  ];
};

const getEapOptions = (filtersData: FiltersData) => {
  if (filtersData && filtersData[FilterCollectionModel.AREA_OF_EXPERTISE]) {
    return filtersData[FilterCollectionModel.AREA_OF_EXPERTISE].options.find(
      (option: Option) => option.dnt_label === EAP_OPTION_LABEL
    );
  }

  return null;
};

export const getPlanTypeOptions = (
  planInfo: unknown,
  coverageType: string
): {
  label: string;
  dnt_label: string;
  value: string;
  isDisabled: boolean;
}[] => {
  if (planInfo && Object.values(planInfo).length > 1) {
    return (
      Object.values(planInfo) as {
        planName: string;
        coverageTypeReciprocityIdMap: { [key: string]: string };
      }[]
    )
      .filter(
        (plan) => plan && plan?.planName && plan?.coverageTypeReciprocityIdMap
      )
      .map((plan) => ({
        label: plan?.planName,
        dnt_label: plan?.planName,
        value: plan?.coverageTypeReciprocityIdMap?.[coverageType] || '',
        isDisabled: !plan?.coverageTypeReciprocityIdMap?.[coverageType],
      }));
  }
  return [];
};

export const transformFiltersData = (
  translate,
  filtersCollection: ProviderFiltersType[],
  filtersData: FiltersData = {},
  lastModifiedFilter: LastModifiedFilter = {},
  coverageType: string,
  selectedFilters?: ProviderSearchFilters,
  planInfo?: any, // <-- add this parameter
  bhProgramLagoonData?,
  searchResults?: any
) => {
  const suppressVisionWeekendEveningsFilter =
    isVisionWeekendEveningsFilterEnabled(coverageType);

  const readLabelFromTranslations: string[] = [
    FilterCollectionModel.ACCEPTING_NEW_PATIENTS,
    FilterCollectionModel.GENDER_CODE,
    FilterCollectionModel.ESSENTIAL_COMMUNITY_PROVIDER,
    FilterCollectionModel.BOARD_CERTIFIED_SPECIALITY_INDICATOR,
  ];
  const [enableBhGroupedAoeFilter] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
  ]);
  const isBhGroupedAoeCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
  const isBhAoeGrouped = enableBhGroupedAoeFilter && isBhGroupedAoeCoverage;

  const filters: ProviderFiltersType[] = [];
  const hasPlatinumProviders =
    checkForPlatinumProvidersInResults(searchResults);
  filtersCollection.forEach((filter) => {
    let filterOptions: Option[] = [];
    if (!!filter.isDisabled) return;
    if (
      filter.model === lastModifiedFilter?.model &&
      !(
        isBhAoeGrouped &&
        filter.model === FilterCollectionModel.AREA_OF_EXPERTISE
      )
    ) {
      filterOptions.push(...getModifiedFilterOptions(lastModifiedFilter));
    } else if (filter.model === FilterCollectionModel.EVENING_AND_WEEKEND) {
      const options = getEveningAndWeekendFilter(
        filtersData,
        translate,
        suppressVisionWeekendEveningsFilter
      );
      filterOptions.push(...options);
    } else if (
      filter.model === FilterCollectionModel.PLATINUM_PROVIDER_INDICATOR
    ) {
      if (hasPlatinumProviders) {
        const platinumOptions = getPlatinumOptionsFromLagoon(translate);
        filterOptions.push(...platinumOptions);
      }
    } else if (filter.model === FilterCollectionModel.PRIORITY_SCHEDULING) {
      const providerSchedulingOptions =
        getProviderSchedulingOptionsFromLagoon(translate);
      filterOptions.push(...providerSchedulingOptions);
    } else if (filter.model === FilterCollectionModel.EAP_OFFERED) {
      const eapOption = getEapOptions(filtersData);
      if (eapOption) {
        filterOptions.push(eapOption);
      }
    } else if (isBehavioralFacilityFilterModel(filter.model)) {
      const options = getBehavioralFacilityProgramsOptions(
        filter,
        filtersData,
        translate,
        selectedFilters,
        bhProgramLagoonData
      );
      filterOptions.push(...options);
    } else if (readOptionsFromFilterCollection().includes(filter.model)) {
      filterOptions.push(...filter.options);
    } else if (filter.model == FilterCollectionModel.GENDER_CODE) {
      const desiredGenderOrder = {
        Male: 0,
        Female: 1,
        Non_Binary: 2,
        Other: 3,
      };
      const sortedOptions = filtersData[filter.model]?.options?.sort(
        (a, b) =>
          desiredGenderOrder[a?.dnt_label] - desiredGenderOrder[b?.dnt_label]
      );
      const translatedOptions = (sortedOptions || []).map((option) => ({
        ...option,
        label: translate(`FILTER_OPTIONS.${filter.model}.${option?.value}`),
      }));
      filterOptions.push(...(translatedOptions || []));
    } else if (filter.model === FilterCollectionModel.PLAN_TYPE) {
      const planTypeOptions = getPlanTypeOptions(planInfo, coverageType);
      filterOptions.push(...planTypeOptions);
    } else if (filter.model === FilterCollectionModel.NETWORK_AFFILIATIONS) {
      // Get options from API response
      if (filtersData[filter.model]?.options) {
        filtersData[filter.model]?.options?.forEach((option: Option) => {
          filterOptions.push({
            label: option?.label,
            value: option?.value,
            dnt_label: option?.dnt_label,
          });
        });
      }
    } else {
      if (filtersData[filter.model]?.options) {
        filtersData[filter.model]?.options?.forEach((option: Option) => {
          if (
            [
              FilterCollectionModel.RACE_CODE,
              FilterCollectionModel.ETHNICITY_CODE,
            ].includes(filter.model as FilterCollectionModel) &&
            option?.dnt_label === NOT_AVAILABLE
          )
            return;
          let label;
          if (readLabelFromTranslations.includes(filter.model)) {
            label = translate(
              `FILTER_OPTIONS.${filter.model}.${option?.value}`
            );
          } else {
            label = option?.label;
          }
          if (label)
            filterOptions.push({
              label,
              value: option?.value,
              dnt_label: option?.dnt_label,
            });
        });
      }
    }

    if (shouldApplySorting(filter)) {
      filterOptions = sortBy(filterOptions, 'label');
    }
    if (filterOptions.length)
      filters.push({ ...filter, options: [...filterOptions] });
  });
  return filters;
};
const checkForPlatinumProvidersInResults = (searchResults?: any): boolean => {
  if (!searchResults || !Array.isArray(searchResults)) {
    return false;
  }
  const hasPlatinum = searchResults.some((provider) => {
    const isPlatinum = provider?.isPlatinumProvider === true;
    return isPlatinum;
  });
  return hasPlatinum;
};

/**
 * @param configValue - Raw config value from Lagoon (comma-separated string)
 * @returns Array of trimmed strings, or empty array if configValue is falsy
 */
export const parseLagoonConfigArray = (
  configValue: string | null | undefined
): string[] =>
  configValue
    ? configValue
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean)
    : [];

export const getLagoonConfigValue = (allConfigs, key) => {
  const config = allConfigs(key);
  return config ? config.value : '';
};

export const getDocAsapLagoonKeys = (
  key: string,
  portalName: any,
  bccFilterSelected = false
): string => {
  if (bccFilterSelected) return `${key}_BCC`;
  if (portalName) return `${key}_${portalName}`;
  return key;
};

/**
 *
 * @deprecated
 * This function is deprecated. Please use `useCurrentMemberOrDependentInfo` instead.
 *
 * import { useCurrentMemberOrDependentInfo } from `useCurrentMember`
 *
 */
export const getCurrentMemberData = (dependentSeqNbr) =>
  useCurrentMemberOrDependentInfo(dependentSeqNbr);

export const getPlanYear = (member: any, coverageType: string) => {
  const coverageData =
    member?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.filter(
      (covType) => covType?.typeCode?.coverageTypeCode === coverageType
    );

  const startDate = coverageData?.[0]?.coverageEffDateRange?.startDate;
  const planYear = startDate?.split('-')?.[0];

  if (!planYear) {
    return null;
  }

  const parsedYear = parseInt(planYear, 10);

  return isNaN(parsedYear) ? null : parsedYear;
};

export const getLocale = () => getLanguage()?.code || 'en';

export const getLob = (
  memberLob: string | undefined,
  storeLob: string | undefined
) => memberLob || storeLob;

export const getOrgTypeCodes = (includeOrgTypeCodes) =>
  isArray(includeOrgTypeCodes)
    ? includeOrgTypeCodes.length
      ? includeOrgTypeCodes
      : []
    : includeOrgTypeCodes
    ? JSON.parse(includeOrgTypeCodes)
    : [];

export const getSearchRadius = (searchRadius) =>
  searchRadius ? +searchRadius : DEFAULT_SEARCH_RADIUS;

export const getSpecialityRollupCodes = (
  specialtyCode,
  includeSpecialityRollupCodes,
  category
) =>
  (specialtyCode ||
    includeSpecialityRollupCodes ||
    (typeof category === 'string'
      ? specialtyCodes[category?.toUpperCase()]
      : '')) ??
  '';

export const getAutoIncrementRadius = (autoIncrementRadius) =>
  autoIncrementRadius
    ? autoIncrementRadius.split(',')
    : AUTO_INCREMENT_SEARCH_RADIUS;

export const getRollUpCodeCategory = (code) => {
  const rollUpCode = code.substring(0, 2);
  if (code === '52-526-033') return [ProviderTypes.ORGANIZATION];
  return (
    rollUpCodeMapping[rollUpCode] || [
      ProviderTypes.PRACTITIONER,
      ProviderTypes.ORGANIZATION,
      ProviderTypes.MEDICAL_GROUP,
    ]
  );
};

export const getProviderTypeRollUpCodes = (
  selectedCategoryRollUpCodes,
  cleanUpProviderCalls
) => {
  const practitionerRollUpCodes = <string[]>[],
    organizationRollUpCodes = <string[]>[],
    mgRollUpCodes = <string[]>[];
  if (selectedCategoryRollUpCodes && cleanUpProviderCalls) {
    selectedCategoryRollUpCodes.forEach((code) => {
      const rollUpCodeCategories = getRollUpCodeCategory(code);
      rollUpCodeCategories.forEach((rollUpCodeCategory) => {
        switch (rollUpCodeCategory) {
          case ProviderTypes.PRACTITIONER:
            practitionerRollUpCodes.push(code);
            break;
          case ProviderTypes.ORGANIZATION:
            organizationRollUpCodes.push(code);
            break;
          case ProviderTypes.MEDICAL_GROUP:
            mgRollUpCodes.push(code);
            break;
          default:
            practitionerRollUpCodes.push(code);
            organizationRollUpCodes.push(code);
            mgRollUpCodes.push(code);
            break;
        }
      });
    });
  }
  return [practitionerRollUpCodes, organizationRollUpCodes, mgRollUpCodes];
};

export const formProviderTypeIncludeRollUpCode = (
  rollUpCodes: string[],
  allRollUpCodes: string[],
  choosePCP: boolean,
  cleanUpProviderCalls: boolean
) => {
  if (choosePCP) {
    return allRollUpCodes;
  } else if (cleanUpProviderCalls) {
    return rollUpCodes;
  } else {
    return allRollUpCodes;
  }
};

export const getLlmSpecialtyParams = ({
  featureFlags,
  providerType,
  chipValue,
  fromLandingPage = false,
  isPseudoRollupSearch = false,
}: {
  featureFlags: [{ key: string; active: boolean }];
  providerType: string;
  chipValue: string;
  fromLandingPage?: boolean;
  isPseudoRollupSearch?: boolean;
}): { invokeLlmModel: boolean; disableLlmSpecialtyFilter: boolean } => {
  const llmSpecialityFilterToggle = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.LLM_SPECIALITY_FILTER_TOGGLE
  );
  const llmFlag = getCategoryLLMFlag(featureFlags, providerType, chipValue);
  return {
    invokeLlmModel:
      isPseudoRollupSearch || fromLandingPage
        ? false
        : !llmSpecialityFilterToggle && llmFlag,
    disableLlmSpecialtyFilter:
      isPseudoRollupSearch || fromLandingPage
        ? false
        : llmSpecialityFilterToggle && llmFlag,
  };
};

export const getProviderSearchParams = (
  customParams,
  selectedCategoryPayload,
  searchType,
  choosePCP,
  cleanUpProviderCalls,
  providerQueryTypes,
  enableCategoryCountV2,
  featureFlags
) => {
  const pcpIndicator =
    customParams?.useNewAgeAndGenderRollups &&
    customParams?.isPCPLandingPage &&
    !choosePCP
      ? null
      : choosePCP || selectedCategoryPayload?.pcpIndicator;
  const params = {
    ...customParams,
    searchType,
    pcpIndicator,

    coverageType: choosePCP
      ? customParams?.coverageType
      : selectedCategoryPayload?.coverages?.[0],
    reciprocityId: choosePCP
      ? customParams?.reciprocityId
      : selectedCategoryPayload?.reciprocityId,
    providerName: !customParams.isUserTextSearch
      ? customParams.providerName
      : '',
    providerQueryTypes,
  };
  const selectedCategoryRollUpCodes =
    selectedCategoryPayload?.includeSpecialityRollupCodes || [];

  const [practitionerRollUpCodes, organizationRollUpCodes, mgRollUpCodes] =
    getProviderTypeRollUpCodes(
      selectedCategoryRollUpCodes,
      cleanUpProviderCalls
    );

  const allRollUpCodes = choosePCP
    ? customParams?.includeSpecialityRollupCodes
    : selectedCategoryPayload?.includeSpecialityRollupCodes;

  const practitionerRollUpCodesInPayload = formProviderTypeIncludeRollUpCode(
    practitionerRollUpCodes,
    allRollUpCodes,
    choosePCP,
    cleanUpProviderCalls
  );

  const practitionerParams = {
    ...params,
    typeaheadAoeCodes: enableCategoryCountV2
      ? getProviderAoeCodesV2({
          ...params,
          providerType: ProviderTypes.PRACTITIONER,
        })
      : getProviderAoeCodes({
          ...params,
          providerType: ProviderTypes.PRACTITIONER,
        }),
    providerType: ProviderTypes.PRACTITIONER,
    includeSpecialityRollupCodes: practitionerRollUpCodesInPayload,
    ...getLlmSpecialtyParams({
      featureFlags,
      providerType: 'practitioner',
      chipValue: selectedCategoryPayload?.label,
    }),
  };

  const organizationRollUpCodesInPayload = formProviderTypeIncludeRollUpCode(
    organizationRollUpCodes,
    allRollUpCodes,
    choosePCP,
    cleanUpProviderCalls
  );

  const orgParams = {
    ...params,
    typeaheadAoeCodes: [],
    providerType: ProviderTypes.ORGANIZATION,
    excludeOrgTypeCodes: choosePCP
      ? customParams?.excludeOrgTypeCodes
      : selectedCategoryPayload?.excludeOrgTypeCodes,
    includeOrgTypeCodes: choosePCP
      ? customParams?.includeOrgTypeCodes
      : selectedCategoryPayload?.includeOrgTypeCodes,
    includeSpecialityRollupCodes: organizationRollUpCodesInPayload,
    ...getLlmSpecialtyParams({
      featureFlags,
      providerType: 'facility',
      chipValue: selectedCategoryPayload?.label,
    }),
  };

  const medGrpRollUpCodesInPayload = formProviderTypeIncludeRollUpCode(
    mgRollUpCodes,
    allRollUpCodes,
    choosePCP,
    cleanUpProviderCalls
  );

  const medGrpParams = {
    ...params,
    typeaheadAoeCodes: enableCategoryCountV2
      ? getProviderAoeCodesV2({
          ...params,
          providerType: ProviderTypes.MEDICAL_GROUP,
        })
      : getProviderAoeCodes({
          ...params,
          providerType: ProviderTypes.MEDICAL_GROUP,
        }),
    providerType: ProviderTypes.MEDICAL_GROUP,
    includeSpecialityRollupCodes: medGrpRollUpCodesInPayload,
    ...getLlmSpecialtyParams({
      featureFlags,
      providerType: 'medical_group',
      chipValue: selectedCategoryPayload?.label,
    }),
  };
  return { practitionerParams, orgParams, medGrpParams };
};

export const getProviderFilterOptionsWithEAPCheck = (filtersData) => {
  const AOEFilters = filtersData[FilterCollectionModel.AREA_OF_EXPERTISE];
  const AOEOptions = AOEFilters?.['options'];
  if (AOEOptions) {
    AOEFilters['options'] = AOEOptions.filter(
      (option) => option?.dnt_label !== EAP_OPTION_LABEL
    );
  }
  return filtersData;
};

const getBehavioralHealthGroupedAoeFilterOptions = (
  filtersData,
  translate?
) => {
  const aoeFilters = filtersData[FilterCollectionModel.AREA_OF_EXPERTISE];
  const aoeOptions = aoeFilters?.['options'];

  if (!aoeOptions) return filtersData;

  const seenLabels = new Set<string>();
  const updatedOptions: any[] = [];

  for (const option of aoeOptions) {
    const code = String(option.value);
    const rawLabel = staticAoeCode[code];

    if (!rawLabel) {
      updatedOptions.push(option);
      continue;
    }

    const label = translate ? translate(rawLabel) : rawLabel;

    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    let newCode = code;
    Object.entries(mapAoeCode).forEach(([key, value]) => {
      if (value.includes(code)) {
        newCode = key;
      }
    });

    updatedOptions.push({
      label,
      value: newCode,
      dnt_label: label,
    });
  }

  return {
    ...filtersData,
    [FilterCollectionModel.AREA_OF_EXPERTISE]: {
      ...aoeFilters,
      options: updatedOptions,
    },
  };
};

export const getProviderFilterOptions = (
  providerFilters,
  featureFlags,
  coverageType,
  translate?
) => {
  const portalShowEAPfilter = usePortalShowEAPfilter();
  if (!providerFilters) return {};

  const enableEmployeeAssistanceProgramAcceptedFilter = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_EMPLOYEE_ASSISTANCE_PROGRAM_ACCEPTED_FILTER
  );

  const isMemberNotEAPEligible = !isMemberEAPEligible() && !portalShowEAPfilter;

  if (
    isMemberNotEAPEligible ||
    !enableEmployeeAssistanceProgramAcceptedFilter
  ) {
    // If EAP feature flag is false or member is not eligible, remove EAP option from AOE filter
    providerFilters = getProviderFilterOptionsWithEAPCheck(providerFilters);
  }
  // add aoe options for behavioral health filters if feature flag is enabled
  const enableBhGroupedAoeFilter = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER
  );
  const isBhGroupedAoeCoverage =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
  if (enableBhGroupedAoeFilter && isBhGroupedAoeCoverage) {
    providerFilters = getBehavioralHealthGroupedAoeFilterOptions(
      providerFilters,
      translate
    );
  }
  return providerFilters;
};

export const getMemberHealthCoverageType = (eligibility, coverageTypesCode) =>
  eligibility
    ?.flatMap((coverage) => coverage?.memberHealthCoverage?.coverageType)
    .find(
      (coverageType) =>
        coverageType?.typeCode?.coverageTypeCode ===
        ReverseCoverageTypesCodes[coverageTypesCode]
    );

export const isValidPlanVariationCode = (
  eligibilitySystemTypeCode: string,
  claimSystemTypeCode: string,
  planVariationCode: string | undefined,
  lob: string | undefined,
  population: string,
  isUmrPopulation: boolean,
  isIFPEarlyPay: boolean = false,
  isGuestUser: boolean = false,
  isEniSsoMemberPopulation: boolean = false,
  isEligibleForStandaloneDental = false
): boolean =>
  isEligibleForStandaloneDental ||
  (eligibilitySystemTypeCode === 'CR' && claimSystemTypeCode === 'CR') ||
  (eligibilitySystemTypeCode === 'MR' && claimSystemTypeCode === '02') ||
  lob?.toUpperCase() === 'OBH' ||
  !!planVariationCode ||
  population === 'USP' ||
  population === 'ENI_Nice' ||
  isUmrPopulation ||
  isEniSsoMemberPopulation ||
  isGuestUser ||
  (planVariationCode === undefined && isIFPEarlyPay);

export const getSortByValue = (isSmartChoice: boolean) =>
  isSmartChoice ? SORT_BY_RECOMMENDED_PROVIDERS : DEFAULT_SORT_BY;

export const getRecommendationPayload = (rollUpCodes?: any) => {
  const [smartChoiceSuppressOHPDSpecialty] = useFeatureFlag([
    ConstantsLagoon?.FEATURE_FLAGS?.SMART_CHOICE_SUPPRESS_OHPD_SPECIALTY,
  ]);

  let isSuppressCostEfficiency = '';
  if (rollUpCodes !== undefined && rollUpCodes !== null) {
    const rollUpCodeValue = rollUpCodes ?? '';

    if (
      rollUpCodeValue !== '' &&
      (Array.isArray(rollUpCodeValue) ? rollUpCodeValue.length > 0 : true)
    ) {
      isSuppressCostEfficiency =
        getSuppressCostEfficiencySmartChoice(rollUpCodeValue);
    }
  }
  const preferenceData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);

  const selectedMember = getLoggedInMember();
  const allConfigs = useLagoon('config')();
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;

  const premiumSuppressionState = allConfigs?.find(
    (row) => row.key === 'SMART_CHOICE_SUPRESS_PREMIUM'
  )?.value;

  const { stateCode } = getGeoLocationFromStorage();
  const isPremiumCareSuppressed = isSuppressPremiumSmartchoice(
    premiumSuppressionState,
    stateCode
  );

  const isAcoBenefitPlan = getAcoBenefitPlan(
    getProductCodeForCoverageType(
      selectedMember,
      ReverseCoverageTypesCodes.MEDICAL
    ) ?? '',
    getMarketType(selectedMember),
    selectedMember?.membershipCategory,
    selectedMember?.population,
    acoBenefitPlanCollection
  );
  const suppressValue =
    isPremiumCareSuppressed === 'Y' || isSuppressCostEfficiency === 'Y'
      ? 'Y'
      : 'N';

  const payload = {
    ...preferenceData,
    hasSetPreferences: preferenceData?.hasSetPreferences === 'y' ? 'y' : 'n',
    hasDismissedCoachMark:
      preferenceData?.hasDismissedCoachMark === 'y' ? 'y' : 'n',
    evngApptAvbl: preferenceData?.evngApptAvbl === 'y' ? 'y' : 'n',
    wkndApptAvbl: preferenceData?.wkndApptAvbl === 'y' ? 'y' : 'n',
    onlineApptSchdlAvbl:
      preferenceData?.onlineApptSchdlAvbl === 'y' ? 'y' : 'n',
    virtualApptAvbl: preferenceData?.virtualApptAvbl === 'y' ? 'y' : 'n',
    providerBoardCertStatus:
      preferenceData?.providerBoardCertStatus === 'y' ? 'y' : 'n',
    distance: preferenceData?.distance,
    languagesSpoken: preferenceData?.languagesSpoken,
    providerGender: preferenceData?.providerGender,
    tieredPlan: '',
    acoBenefitPlan: isAcoBenefitPlan ? 'y' : 'n',
    randomizationKey: preferenceData?.memberRandomizedKey,
    suppressPremium: smartChoiceSuppressOHPDSpecialty
      ? suppressValue
      : isPremiumCareSuppressed,
    suppressCostEfficiency: smartChoiceSuppressOHPDSpecialty
      ? suppressValue
      : undefined,
    applyUpdatedTierLogicToggle: 'Y',
  };
  if (!smartChoiceSuppressOHPDSpecialty) {
    delete payload.suppressCostEfficiency;
  }

  delete payload.memberRandomizedKey;
  delete payload.createdDate;
  delete payload.lastUpdatedDate;
  delete payload.uuid;
  delete payload.hasDefaultPreferences;

  return payload;
};

export const memberCatRPK = (
  currentMember,
  uspTierOneFlag,
  uspToggleFlag,
  tierBenefits,
  providerType
) => {
  const membershipCategory = uspToggleFlag
    ? currentMember?.membershipCategory || ''
    : null;
  return {
    membershipCategory,
    rulesPackageKey: getRulesPackageKey(
      currentMember,
      tierBenefits,
      providerType,
      uspTierOneFlag
    ),
  };
};

export const getPlanVariationCodeForSearchParams = (
  planVariationCode,
  planVariationCodeNew
) => planVariationCode || planVariationCodeNew;

export const getFirstNumber = (str?: string | null) => str?.split(',')[0];

export const filterSearchResults = (searchResult) => {
  const providerSearchResult = get(searchResult, 'providerSearch', {});

  const providersData = providerSearchResult.providers;

  const practitionersFiltered = providersData?.filter(
    (data) =>
      data?.providerType === ProviderType.PRACTITIONER ||
      data?.providerType === null
  );

  const facilitiesFiltered = providersData?.filter(
    (data) => data?.providerType === ProviderType.ORGANIZATION
  );

  const medicalGroup = providersData?.filter(
    (data) => data?.providerType === ProviderType.MEDICAL_GROUP
  );

  return {
    totalCountPractitioners: providerSearchResult.totalCountPractitioners,
    totalCountOrganizations: providerSearchResult.totalCountOrganizations,
    totalCountMedicalGroup: providerSearchResult.totalCountMedicalGroup,
    providers: practitionersFiltered || [],
    facilities: facilitiesFiltered || [],
    medicalGroup: medicalGroup || [],
    practitionerSearchRadius: providerSearchResult.practitionerSearchRadius,
    organizationSearchRadius: providerSearchResult.organizationSearchRadius,
    medicalGroupSearchRadius: providerSearchResult.medicalGroupSearchRadius,
    networkStatus: providerSearchResult.networkStatus,
    isTieredPlan: providerSearchResult.isTieredPlan,
    uniqueSearch: providerSearchResult.uniqueSearch,
  };
};

export const getProviderAoeCodesV2 = (params) => {
  let aoeCodes = [];

  if (params.searchType === Constants.SEARCH_TYPES.SPECIALTY) {
    aoeCodes = params.typeaheadAoeCodes;
  }

  return aoeCodes;
};

export const getProviderAoeCodes = (params) => {
  let aoeCodes = [];

  const providerQueryType = params.providerQueryTypes?.filter(
    (item) => ProviderTypes[item.providerType] === params.providerType
  )[0]?.queryType;

  if (
    params.searchType === Constants.SEARCH_TYPES.SPECIALTY &&
    providerQueryType === Constants.SEARCH_TYPES.AOE
  ) {
    aoeCodes = params.typeaheadAoeCodes;
  }

  return aoeCodes;
};
export const getBehavioralFacilityProgramsProviderSearchV2 = (params) => {
  let bhfpcodes = '';
  if (params.searchType === Constants.SEARCH_TYPES.SPECIALTY) {
    bhfpcodes = params.bhProgramFacilityAgg;
  }

  return bhfpcodes;
};

export const getBehavioralFacilityProgramsProviderSearch = (params) => {
  let bhfpcodes = '';

  const providerQueryType = params.providerQueryTypes?.filter(
    (item) => ProviderTypes[item.providerType] === params.providerType
  )[0]?.queryType;

  if (
    params.searchType === Constants.SEARCH_TYPES.SPECIALTY &&
    providerQueryType === Constants.SEARCH_TYPES.BHFP
  ) {
    bhfpcodes = params.bhProgramFacilityAgg;
  }

  return bhfpcodes;
};

export const getIncludeSpecialityRollupCodes = (
  { behavioralFacilityPrograms = '', searchType },
  includeSpecialityRollupCodesFromParams,
  typeaheadAoeCodes = [],
  enableCategoryCountV2 = false
) =>
  (behavioralFacilityPrograms === '' &&
    searchType === Constants.SEARCH_TYPES.SPECIALTY &&
    typeaheadAoeCodes.length <= 0) ||
  enableCategoryCountV2
    ? resolveIncludeSpecialityRollupCodes(
        includeSpecialityRollupCodesFromParams
      )
    : [];
export const getProviderIds = (providerIds) => providerIds || '';
export const getMemberEffectiveDate = (
  isPreEffective,
  currentMemberEffectiveDate
) => (isPreEffective ? currentMemberEffectiveDate : null);
export const getExcludeNatAncForNetworkIds = (excludeNatAncForNetworkIds) =>
  excludeNatAncForNetworkIds !== ''
    ? excludeNatAncForNetworkIds?.replace(/\s/g, '')?.split(',')
    : [];

export const getRollupCodeMap = (rollupCodeMapLagoonData: any) =>
  rollupCodeMapLagoonData?.reduce((acc, item) => {
    if (item.source && item.destination) {
      acc[item.source] = item.destination;
    }
    return acc;
  }, {});
export const validateRadius = (providerSearchRadius, autoSearchRadius) =>
  providerSearchRadius && autoSearchRadius !== providerSearchRadius;

export const validateSourceAndDestination = (source, destination) =>
  source && destination;

export const getUpdatedFilterState = (
  includeOrgTypeCodes: any,
  excludeOrgTypeCodes: any,
  acceptingNewPatients: boolean | undefined,
  validPlanVariationCode: boolean,
  sortByValue: string,
  pageSize: number
) => {
  const updatedParams: ProviderSearchRequestParams = {
    pageNumber: 1,
    pageSize,
    includeOrgTypeCodes: getOrgTypeCodes(includeOrgTypeCodes),
    excludeOrgTypeCodes: getOrgTypeCodes(excludeOrgTypeCodes),
    searchRadius: DEFAULT_SEARCH_RADIUS,
    sortBy: sortByValue,
  };
  // reset filters
  const defaultFilters: FilterCollectionModel[] = [
    FilterCollectionModel.AUTO_SEARCH_RADIUS,
    FilterCollectionModel.DISTANCE,
  ];

  if (acceptingNewPatients) {
    updatedParams.acceptingNewPatients = true;
    defaultFilters.push(FilterCollectionModel.ACCEPTING_NEW_PATIENTS);
  }

  if (!validPlanVariationCode) {
    throw new Error('Plan Variation Code is required');
  }

  return { updatedParams, defaultFilters };
};

export const canGetProviderResults = (
  providerType: string,
  searchParams: any,
  queryParams: any
) =>
  providerType && JSON.stringify(searchParams) !== JSON.stringify(queryParams);

export const getRollupCodes = (
  bhFacilityPrograms,
  rollupCodes = [],
  typeaheadAoeCodes: string[] = [],
  enableCategoryCountV2: boolean = false
) => {
  const rollupCodesFinal = rollupCodes;
  return (bhFacilityPrograms !== '' || typeaheadAoeCodes.length > 0) &&
    !enableCategoryCountV2
    ? []
    : [...new Set(rollupCodesFinal)];
};

export const getUrlParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(searchParams.entries());
};

export const getProviderSearchVariables = ({
  resetFilters,
  defaultSearchFilters,
  selectedFilters,
  pageNumber,
  initialPage,
  firstPageVariables,
  resetFiltersVariables,
  variables,
}) => {
  const useResetFiltersVariables =
    resetFilters &&
    JSON.stringify(defaultSearchFilters) === JSON.stringify(selectedFilters) &&
    pageNumber === 1;

  const useFirstPageVariables =
    initialPage && Object.keys(firstPageVariables || {}).length !== 0;

  if (useResetFiltersVariables) {
    return resetFiltersVariables;
  }

  if (useFirstPageVariables) {
    return firstPageVariables;
  }
  return variables;
};

export function getProviderSearchQueryPreCheckVariables({
  defaultVariables,
  variables,
  initialPage,
  isMissingParameters,
  requiredParams,
  mapLocationsOnly,
  enableMapPinRedesign,
  isSmartChoice,
  queryParams,
  providerType,
  skipQuery,
}) {
  const hasMissingParametersForAutoSearchRadiusQuery =
    isMissingParameters('Provider Search', requiredParams) ||
    (mapLocationsOnly && !enableMapPinRedesign);

  const canMakeAutoSearchRadiusProviderSearchQuery =
    JSON.stringify(defaultVariables) !== JSON.stringify(variables) &&
    !initialPage &&
    !mapLocationsOnly &&
    !skipQuery;
  !hasMissingParametersForAutoSearchRadiusQuery;

  const hasMissingParametersForProviderSearch =
    isMissingParameters('Provider Search', requiredParams) ||
    (mapLocationsOnly && !enableMapPinRedesign) ||
    (isSmartChoice && queryParams?.recommendationPreferences?.hasSetPreference);

  const canMakeProviderSearchQuery =
    providerType &&
    !initialPage &&
    !skipQuery &&
    !hasMissingParametersForProviderSearch;

  return {
    canMakeAutoSearchRadiusProviderSearchQuery,
    canMakeProviderSearchQuery,
  };
}

export const getBehavioralFacilityPrograms = (
  filteredParams: any,
  behavioralFacilityPrograms: string
) => {
  const filteredBehavioralPrograms =
    filteredParams?.behavioralFacilityPrograms?.trim();
  return filteredBehavioralPrograms === ''
    ? ''
    : filteredBehavioralPrograms || behavioralFacilityPrograms || '';
};

export const getFilteredBHFacilityProgramKeywordValue = (
  bhFacilityPrograms: string,
  filterParams: any
): string => {
  if (!bhFacilityPrograms) return '';

  const behavioralFacilityPrograms =
    filterParams?.behavioralFacilityPrograms?.trim();
  if (!behavioralFacilityPrograms) return bhFacilityPrograms;

  const uniquePrograms = new Set([
    ...bhFacilityPrograms.split(',').map((program) => program.trim()),
    ...behavioralFacilityPrograms.split(',').map((program) => program.trim()),
  ]);

  return Array.from(uniquePrograms).join(',');
};

export const shouldDisablePCPIndAndANP = (
  disablePCPIndAndANP: boolean,
  isPCPLandingPage: boolean,
  choosePCP?: boolean
) => disablePCPIndAndANP && isPCPLandingPage && !choosePCP;

export const resolveFilterSortOption = (
  ResultsHasProviderWithUpcomingAvailability: boolean,
  isSmartChoice: boolean,
  selectedFilters: ProviderSearchFilters,
  coverageType: string,
  translate
) => {
  const BEST_MATCH_SORT_LABEL_KEY = getBestMatchOptionKey(coverageType);
  const SMART_CHOICE_SORT_LABEL_KEY =
    getRecommendedProviderOptionKey(coverageType);
  const sortDefaultOption = isSmartChoice
    ? {
        label: translate(`SORT_OPTIONS.${SMART_CHOICE_SORT_LABEL_KEY}`),
        value: Constants.RECOMMENDED_PROVIDERS_SEARCH_SORT_BY,
        hide: true,
        dnt_label: translationsEnJson.SORT_OPTIONS[SMART_CHOICE_SORT_LABEL_KEY],
      }
    : {
        label: translate(`SORT_OPTIONS.${BEST_MATCH_SORT_LABEL_KEY}`),
        value: Constants.PROVIDER_SEARCH_SORT_BY,
        hide: true,
        dnt_label: translationsEnJson.SORT_OPTIONS[BEST_MATCH_SORT_LABEL_KEY],
      };

  return selectedFilters.SortBy?.value ===
    SortOptionValues.UPCOMING_AVAILABILITY &&
    !ResultsHasProviderWithUpcomingAvailability
    ? {
        ...selectedFilters,
        SortBy: sortDefaultOption,
      }
    : selectedFilters;
};

export const getRegionalVirtualCareProviderArray = (
  chipValue: string,
  allConfigs: any[]
): string[] => {
  if (!chipValue) return [];
  const regionalVirtualCareProvider = allConfigs?.find(
    (row) => row.key === 'VIRTUAL_CARE_CHIP_AND_REGIONAL_PROVIDER'
  );
  let value = '';
  if (regionalVirtualCareProvider?.value) {
    try {
      const parsed = JSON.parse(regionalVirtualCareProvider.value);
      value = parsed?.[chipValue] ?? '';
    } catch {
      value = '';
    }
  }
  return value
    ? value
        .split(',')
        .map((v: string) => v.trim())
        .filter(Boolean)
    : [];
};

export const shouldBlockNationalProviders = (
  chipValue: string,
  enableVirtualCareEpic: Boolean,
  isVirtualCareUnetRailroadSuppressed: Boolean,
  allConfigs: any[]
): Boolean => {
  if (!chipValue) return false;
  const regionalVirtualCareProvider = allConfigs?.find(
    (row) => row.key === 'BLOCK_VIRTUAL_CHIP_NATIONAL_PROVIDERS'
  );
  let value = false;
  if (regionalVirtualCareProvider?.value) {
    try {
      const parsed = JSON.parse(regionalVirtualCareProvider.value);
      value = parsed?.[chipValue] === 'Y' ? true : false;
    } catch {
      value = false;
    }
  }
  return enableVirtualCareEpic && !isVirtualCareUnetRailroadSuppressed && value;
};

export const shouldSuppressAddressAndMiles = (
  enableVirtualCareEpic: boolean,
  isVirtualCareUnetRailroadSuppressed: boolean,
  virtualIndicator: string = '',
  chipValue: string,
  eligibilities: any,
  allConfigs: any[]
): boolean => {
  const isEligibilitiesNotEmpty: boolean =
    Array.isArray(eligibilities) && eligibilities.length > 0;
  if (!isEligibilitiesNotEmpty) return false;
  if (isVirtualCareUnetRailroadSuppressed) return false;
  if (!virtualIndicator && !chipValue) return false;
  const regionalProviderArray = getRegionalVirtualCareProviderArray(
    chipValue,
    allConfigs
  );
  return (
    enableVirtualCareEpic &&
    regionalProviderArray.some((rp) => virtualIndicator.includes(rp))
  );
};
export const shouldEnableRegionalVirtualCareSection = (
  enableVirtualCareEpic: boolean,
  isVirtualCareUnetRailroadSuppressed: boolean,
  regionalProviderArray: string[]
): boolean =>
  enableVirtualCareEpic &&
  regionalProviderArray?.length > 0 &&
  !isVirtualCareUnetRailroadSuppressed;

export const getSearchSuggestion = (flagEnabled: boolean, suggestion: string) =>
  flagEnabled ? suggestion : '';

export type UesVirtualCareKeywordMappingTable = {
  id?: string;
  key?: string;
  keyword: string;
  pseudoRollupCode: string;
  serviceType: string;
  pesCode?: string;
  chips: string;
  'lagoon.updatedBy'?: string;
  'lagoon.updatedAt'?: string;
};

export type Eligibility = {
  careGroup: string;
  serviceType: string;
  providers: any[];
};

export const findCommonCodes = (
  rollupCodes: string[] = [],
  chipWisePseudoRollupCode: string[] = []
) => {
  if (!rollupCodes.length || !chipWisePseudoRollupCode.length) return [];

  return (
    rollupCodes?.filter((code) => chipWisePseudoRollupCode.includes(code)) ?? []
  );
};

export const getCategoryVirtualCareProvidersFromEligibilities = ({
  search = '',
  includeSpecialityRollupCodes = '',
  uesVirtualCareKeywordMappingTable = [],
  eligibilities = [],
  enablePseudoRollupCodeOnlyMatch = '',
  filterVcProvidersForPrimaryCareChip = '',
  chipValue = '',
  showAllVirtualCareList = false,
}: {
  search: string;
  includeSpecialityRollupCodes: string;
  uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[];
  eligibilities: Eligibility[];
  enablePseudoRollupCodeOnlyMatch: string;
  filterVcProvidersForPrimaryCareChip?: string;
  chipWisePseudoRollupCode?: string[];
  chipValue?: string;
  showAllVirtualCareList?: boolean;
}): Eligibility[] => {
  const chipWisePseudoRollupCode =
    getChipWisePseudoRollupCodeForVirtualProvider(
      uesVirtualCareKeywordMappingTable,
      chipValue
    );
  const rollupCodesToCompare = showAllVirtualCareList
    ? chipWisePseudoRollupCode
    : includeSpecialityRollupCodes?.split(',');
  if (
    !uesVirtualCareKeywordMappingTable.length ||
    !eligibilities.length ||
    !rollupCodesToCompare?.length ||
    !chipWisePseudoRollupCode.length
  ) {
    return [];
  }

  // Split comma-separated rollup codes into an array

  let commonCodes = findCommonCodes(
    rollupCodesToCompare,
    chipWisePseudoRollupCode
  );
  if (
    commonCodes.length > 1 &&
    chipValue?.includes(CareCategories.PRIMARY_CARE)
  ) {
    switch (filterVcProvidersForPrimaryCareChip?.toLowerCase()) {
      case CareCategories.FACILITIES.toLowerCase():
        commonCodes = commonCodes.filter((code) => code === '50-999-100');
        break;
      default:
        // Keep Primary Care by default
        commonCodes = commonCodes.filter((code) => code === '01-999-100');
        break;
    }
  }
  const codesToCheck = commonCodes.length > 0 ? commonCodes : [];
  // Find matching entry directly in the array
  const matchedEntries = getMatchingVirtualCareEntries({
    uesVirtualCareKeywordMappingTable,
    searchTerm: search,
    vertical: codesToCheck,
    enablePseudoRollupCodeOnlyMatch,
  });

  if (matchedEntries?.length > 0) {
    // Find eligibilities for all matched service types
    const foundEligibilities = eligibilities.filter((el) =>
      matchedEntries.some(
        (entry) =>
          el?.serviceType.toLowerCase() === entry?.serviceType.toLowerCase()
      )
    );
    return foundEligibilities;
  }
  return [];
};

const getChipWisePseudoRollupCodeForVirtualProvider = (
  uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[] = [],
  chipValue = ''
) => {
  const chipCategory =
    Constants.CARE_CATEGORIES_NAME_PAYLOAD.find(
      (item) => item.label === chipValue
    )?.chipCategory || chipValue;
  const chipPseudoRollupCode = uesVirtualCareKeywordMappingTable
    .filter((item) => item.chips?.includes(chipCategory))
    .map((item) => item.pseudoRollupCode);
  return chipPseudoRollupCode;
};

export const getVirtualCareLicenseStateCodes = (
  regionalProviderArray: string[]
): string => {
  const { stateCode } = getGeoLocationFromStorage();
  return regionalProviderArray?.length ? stateCode : '';
};

export const getRollupCodesForVCProviderSearch = (
  search: string,
  specialityCode = '',
  pseudoRollupCodeForPrimaryCareLandingPage: string
): string => {
  let config = {};
  try {
    config = JSON.parse(pseudoRollupCodeForPrimaryCareLandingPage) || {};
  } catch {
    config = {};
  }
  const matchingKey = Object.keys(config).find((key) =>
    search.toLowerCase().includes(key.toLowerCase())
  );
  const vcRollupCode = matchingKey ? config[matchingKey] : '';
  return `${specialityCode},${vcRollupCode}`;
};

export const setAutoIncrementRadiusVariable = (
  isPhoneSearchType: boolean,
  autoIncrementRadius: string[]
): { autoIncrementRadius?: string[] } =>
  isPhoneSearchType ? { autoIncrementRadius } : {};
